import { Alert, AlertTitle, capitalize, Snackbar } from '@mui/material';
import { isErrorResponse } from '@ts-rest/core';
import { isFetchError, isUnknownErrorResponse } from '@ts-rest/react-query/v5';
import type { DateTime } from 'luxon';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import Illustration3 from '~/assets/svgs/illustration-3.svg';
import { OrdersBanner } from '~/components/organisms/OrdersBanner';
import { OrdersTable } from '~/components/organisms/OrdersTable';
import { ROWS_PER_PAGE } from '~/constants';
import { useSnackbar } from '~/hooks/useSnackbar';
import { Logger } from '~/lib/logger';
import { useApi } from '~/providers/ApiProvider';
import type { OrdersTableStatusType } from '~/types/OrdersTableData';
import { ShopifyOrdersDataResponseSchema } from '~/types/ShopifyOrdersData';
import { convertToTableData, getApiStatus } from '~/utils';

const logger = new Logger('B2B Orders Route');

export const ShopifyOrders: FC = () => {
  const { warehouseApi } = useApi();
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<OrdersTableStatusType>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(searchTerm);
  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);

  const { snackbar, updateSnackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const start = useMemo(() => startDate?.toUTC().toISO(), [startDate]);
  const end = useMemo(() => endDate?.toUTC().toISO(), [endDate]);

  // Fetch order items
  const { data, isLoading, isError, error, refetch } = warehouseApi.listOrderItems.useQuery({
    queryKey: ['orderItems', page, status, startDate, endDate, debouncedSearchTerm],
    queryData: {
      query: {
        page: page,
        size: ROWS_PER_PAGE,
        sort: 'status',
        filter: {
          status: getApiStatus(status),
          ...(searchTerm && { text: debouncedSearchTerm }),
          ...(start && { start }),
          ...(end && { end })
        }
      }
    }
  });

  // Approve order items
  const { mutateAsync: mutateAsyncApprove, isPending: isPendingApprove } = warehouseApi.approveOrderItem.useMutation({
    onSuccess: () => {
      showSnackbar('Order item approved', 'success');
      refetch();
    },
    onError: (error) => {
      logger.error('Error approving order item', error);
      showSnackbar('Failed to approve order item', 'error');
    }
  });

  // Reject order items
  const { mutateAsync: mutateAsyncReject, isPending: isPendingReject } = warehouseApi.rejectOrderItem.useMutation({
    onSuccess: () => {
      showSnackbar('Order item rejected', 'success');
      refetch();
    },
    onError: (error) => {
      logger.error('Error rejecting order item', error);
      showSnackbar('Failed to reject order item', 'error');
    }
  });

  const {
    mutateAsync: mutateAsyncExportTable,
    isPending: isPendingOrderItemsExport,
    contractEndpoint: orderItemsExportContractEndpoint
  } = warehouseApi.exportOrderItemsReport.useMutation({
    onSuccess: () => {
      showSnackbar('Export is in progress. You will recieve an email once it is ready.', 'success');
    }
  });

  // Export order items
  const handleTableExport = async () => {
    if (!start || !end) {
      showSnackbar('Please select both start and end dates', 'error');
      logger.error('Failed to export table: missing start or end date');
      return;
    }

    try {
      await mutateAsyncExportTable({
        body: {
          data: {
            startDate: start,
            endDate: end
          }
        }
      });
    } catch (error) {
      const snackbarMessage = 'Failed to export table';

      if (isFetchError(error)) {
        showSnackbar(snackbarMessage, 'error');
        logger.error('Fetch error when attempting to export shopify table', error);
        return;
      }

      if (isUnknownErrorResponse(error, orderItemsExportContractEndpoint)) {
        showSnackbar(snackbarMessage, 'error');
        logger.error('Unknown error when attempting to export shopify table', error);
        return;
      }

      if (isErrorResponse(error) && error.status === 400 && error.body.error.message) {
        showSnackbar(error.body.error.message, 'error');
        logger.error(error.body.error.message, error);
        return;
      }

      // Fallback error handling
      showSnackbar(snackbarMessage, 'error');
      logger.error('Error when attempting to export shopify table', error);
      return;
    }
  };

  const handleCloseSnackbar = () => {
    closeSnackbar();
    setTimeout(() => updateSnackbar(''), 500); // Delay to allow the snackbar to close
  };

  const handleApproveItems = async (ids: string[]) => {
    await Promise.all(
      ids.map((id) =>
        mutateAsyncApprove({
          params: { orderItemId: id },
          body: {}
        })
      )
    );
  };

  const handleRejectItems = async (ids: string[]) => {
    await Promise.all(
      ids.map((id) =>
        mutateAsyncReject({
          params: { orderItemId: id },
          body: {}
        })
      )
    );
  };

  const parsedData = ShopifyOrdersDataResponseSchema.safeParse(data?.body);

  useEffect(() => {
    if (isError) {
      const errorMessage = 'Failed to fetch orders';
      logger.error(errorMessage, error);
      showSnackbar(errorMessage, 'error');
    } else if (!isLoading && parsedData.success === false) {
      logger.error('Failed to parse orders data', parsedData.error);
      showSnackbar('Failed to parse orders data', 'error');
    }
  }, [error, isError, isLoading, parsedData.error, parsedData.success, showSnackbar]);

  const tableData = !isLoading && parsedData.data ? convertToTableData(parsedData.data) : null;
  const totalItems = data?.body.meta.total ?? 0;

  return (
    <>
      <Snackbar
        open={snackbar.isOpen && snackbar.message !== ''}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.type} sx={{ width: '100%' }}>
          {snackbar.type && <AlertTitle>{capitalize(snackbar.type)}</AlertTitle>}
          {snackbar.message}
        </Alert>
      </Snackbar>

      <OrdersBanner
        imageUrl={Illustration3}
        count={totalItems}
        isLoading={isLoading}
        title="Leafio"
        subtitle="Leafio system orders."
      />
      <OrdersTable
        data={tableData}
        isLoading={isLoading}
        page={page}
        setPage={setPage}
        totalItems={totalItems}
        status={status}
        setStatus={setStatus}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setDebouncedSearchTerm={setDebouncedSearchTerm}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        handleApproveItems={handleApproveItems}
        approveLoading={isPendingApprove}
        handleRejectItems={handleRejectItems}
        rejectLoading={isPendingReject}
        handleExport={handleTableExport}
        isExportLoading={isPendingOrderItemsExport}
      />
    </>
  );
};
